import type { FC } from 'react'
import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import IconWrapper from '@segment/matcha/components/IconWrapper'
import { CheckIcon } from '@segment/matcha/components/Icons/Brandbook'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import useMatchMedia from '@segment/utils/useMatchMedia'

const OVERLINE = `Conclusion`
const HEADLINE = `Personalize or perish`
const content = (
  <>
    <CopyText.Description tag="p" variant="h5">
      The State of Personalization makes it clear that brands only face two choices: personalize or perish.
    </CopyText.Description>
    <CopyText.Description tag="p" variant="pMedium">
      The digital acceleration sparked by the pandemic has created a new reality and new expectations. If brands fail to
      take this seriously, customers will simply walk away and support the competition.
    </CopyText.Description>
    <CopyText.Description tag="p" variant="pMedium">
      If you’d like to join the 20,000+ customers using Segment Twilio to power their personalization strategies, get
      started today.
    </CopyText.Description>
  </>
)
const list = [
  `Segment conducted this research using online surveys distributed by RepData.`,
  `Survey respondents were n=2,700 adult consumers who have purchased something online in the past 6 months, and n=300 adult manager+ decision-makers at consumer-facing companies that provide goods and/or services online.`,
  `Respondents were from the United States, United Kingdom, Australia, and New Zealand.`,
  `Data was collected from April 8 to April 20, 2021.`,
]
const PersonalizeOrPerish: FC = () => {
  const theme = useTheme()
  const mobileResolutionMediaQuery = theme.breakpoints.down(`md`, false)
  const isSmallResolution = useMatchMedia(mobileResolutionMediaQuery, false)

  return (
    <SectionBase variant="2Col">
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Overline tag="span" variant="h5">
            {OVERLINE}
          </CopyText.Overline>
          <StyledCopyTextHeading tag="h2" variant="h2">
            {HEADLINE}
          </StyledCopyTextHeading>
          {content}
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <ListWrapper>
          <ColumnTrack gap="small" tag="ul">
            {list.map((item) => (
              <FeatureBoxBase tag="li" alignment="left" headerPosition={isSmallResolution ? `top` : `left`} key={item}>
                <FeatureBox.Header>
                  <IconWrapper elevation={1} variant="circle">
                    <CheckIcon color={theme.palette.twilio.gray[100]} />
                  </IconWrapper>
                </FeatureBox.Header>
                <FeatureBox.Body>
                  <StyledText>{item}</StyledText>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
          </ColumnTrack>
        </ListWrapper>
      </Section.Contents>
    </SectionBase>
  )
}

export default PersonalizeOrPerish

const StyledText = styled(FeatureBox.Text)`
  margin: 0;
  align-self: center;
`

const StyledCopyTextHeading = styled(CopyText.Heading)`
  margin-bottom: 40px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-bottom: 48px;
  }
`

const ListWrapper = styled.div`
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding-top: 48px;
    max-width: 452px;
  }
`
